import { useId, useState, type FormEvent } from 'react';

import { Button } from '@/components/Button/Button';

import { SUPPORT_QUERY_MAX_LENGTH } from './supportConsoleFormat';
import styles from './SupportConsolePage.module.css';

interface SupportSearchFormProps {
  busy: boolean;
  // Called with the trimmed text once it could be an ID or an email.
  onSearch(query: string): void;
}

// What is typed here is somebody's email address. It lives in this component's
// state and nowhere else: not the address bar, not browser storage, and
// (autoComplete off) not the browser's own list of things typed before.
export function SupportSearchForm({ busy, onSearch }: SupportSearchFormProps) {
  const inputId = useId();
  const [value, setValue] = useState('');
  const [error, setError] = useState('');

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const query = value.trim();

    if (query === '') {
      setError('Enter a user ID or an email address.');
      return;
    }
    if ([...query].length > SUPPORT_QUERY_MAX_LENGTH) {
      setError('That is too long to be a user ID or an email address.');
      return;
    }

    setError('');
    onSearch(query);
  }

  return (
    <form
      aria-label="Find a user"
      className={styles.searchForm}
      noValidate
      onSubmit={handleSubmit}
      role="search"
    >
      <div className={styles.field}>
        <label htmlFor={inputId}>User ID or email</label>
        <input
          aria-describedby={error ? `${inputId}-hint ${inputId}-error` : `${inputId}-hint`}
          aria-invalid={Boolean(error)}
          autoCapitalize="none"
          autoComplete="off"
          autoCorrect="off"
          id={inputId}
          onChange={(event) => setValue(event.target.value)}
          spellCheck={false}
          type="text"
          value={value}
        />
        <span className={styles.hint} id={`${inputId}-hint`}>
          The whole ID or the whole email address. Part of one finds nobody.
        </span>
        {error ? (
          <span className={styles.fieldError} id={`${inputId}-error`} role="alert">
            {error}
          </span>
        ) : null}
      </div>
      {/* Outlined, not filled: the filled button's white text falls short of
          4.5:1 on the dark theme's accent, app-wide. */}
      <Button className={styles.searchButton} disabled={busy} type="submit" variant="secondary">
        {busy ? 'Searching…' : 'Search'}
      </Button>
    </form>
  );
}
